import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Store } from '@ngrx/store';
import { EMPTY } from 'rxjs';
import { catchError, filter, map, tap } from 'rxjs/operators';

import { ToastService } from '@libs/src/toast/toast.service';
import { GroupIntroSelectionsFormService } from '@main-client/src/app/group-intro-selections-form/group-intro-selections-form.service';

import { fadeInEnter } from '@libs/src/animations/animations';
import { Account } from '@libs/src/models/account.model';
import { Group } from '@libs/src/models/group.model';
import { MatchConfig } from '@libs/src/models/match.interfaces';
import { Tenant } from '@libs/src/tenant/tenant.interface';
import { SetUser } from '@libs/src/user/user.actions';
import {
  FeedGroupState,
  FeedState,
  IAppState,
} from '@main-client/src/app/app.state';
import { FeedPopulate } from '@main-client/src/app/feed/feed.actions';
import { hasEmptySelections } from '@main-client/src/app/group-intro-selections-form/group-intro-selections-form.helper';
import { MatchSelections } from '@main-client/src/app/group-intro-selections-form/match-selections';
import { isAnySegmentSelectableForMatchingRules } from '@main-client/src/app/user-onboarding/user-onboarding.helper';

import get from 'lodash-es/get';

@Component({
  animations: [fadeInEnter],
  selector: 'app-user-onboarding-view-intro-goals-selection',
  templateUrl: './user-onboarding-view-intro-goals-selection.component.html',
})
export class UserOnboardingViewIntroGoalsSelectionComponent implements OnInit {
  @Input() group: Group;
  @Input() tenant: Tenant;
  @Input() user: Account;
  @Output() navigateToNextView = new EventEmitter();
  selections: MatchSelections = {
    customSegments: {},
    roles: {},
  };
  matchConfig: MatchConfig;
  isLoading = false;
  isSaving = false;
  errorMessage = '';

  constructor(
    private groupIntroSelectionsFormService: GroupIntroSelectionsFormService,
    public store: Store<IAppState>,
    public toastService: ToastService,
  ) {}

  ngOnInit() {
    this.isLoading = true;
    this.checkIfMatchingRulesAreSelectable(this.group);
    const groupId = this.group._id;
    this.store.dispatch(FeedPopulate({ _id: groupId }));
    this.store
      .select('feed')
      .pipe(
        filter(
          (feedState: FeedState) =>
            !!feedState[groupId] &&
            feedState[groupId].isInitialized &&
            !feedState[groupId].isLoading &&
            !feedState[groupId].isCurrentMatchLoading &&
            !feedState[groupId].isPastMatchesLoading,
        ),
        map((feedState: FeedState) => feedState[groupId]),
        map((groupFeedState: FeedGroupState) =>
          get(groupFeedState, ['matchConfigs', 'items', 0]),
        ),
        tap((matchConfig: MatchConfig) => {
          this.matchConfig = matchConfig;
          this.isLoading = false;
        }),
      )
      .subscribe();
  }

  checkIfMatchingRulesAreSelectable(group: Group) {
    if (isAnySegmentSelectableForMatchingRules(group)) {
      return;
    }
    this.navigateToNextView.emit(group);
  }

  save(
    group: Group,
    user: Account,
    selections: MatchSelections,
    matchConfig: MatchConfig,
  ) {
    if (this.isLoading || this.isSaving) {
      return;
    }
    if (hasEmptySelections(selections, matchConfig)) {
      this.errorMessage = `You’re currently not set up to be introduced with anyone. Please change your
    selections.`;
      return;
    }
    this.isSaving = true;
    this.groupIntroSelectionsFormService
      .saveIntroSelections(group, user, selections)
      .pipe(
        tap((updateAccount: Account) => {
          this.store.dispatch(SetUser(updateAccount));
          this.navigateToNextView.emit(group);
          this.isSaving = false;
        }),
        catchError((response) => {
          this.isSaving = false;
          this.toastService.showError(response.error.message);
          return EMPTY;
        }),
      )
      .subscribe();
  }

  selectionChange(selectionChange: MatchSelections) {
    this.errorMessage = '';
    this.selections = selectionChange;
  }
}
